import { Cubit } from "blac-next";
import {
  createCmsClient,
  type CmsApiClient,
  type FeedRequestBody,
  type JourneyData
} from "@9amhealth/cms-api";
import envVariables from "src/lib/envVariables";
import { getSupportedUserLanguage } from "src/lib/i18next";
import { Language } from "src/constants/language";
import { DbJourneyContentService } from "src/lib/db/DbJourneyContentService";
import { JourneyMetaService } from "src/lib/db/JourneyMetaService";
import reportErrorSentry from "src/lib/reportErrorSentry";
import {
  Feed,
  FeedData
} from "src/ui/components/UserEducationalFeed/UserEducationalFeedBloc";
import { StorageController } from "../StorageBloc/StorageBloc";

const JOURNEY_CONTENT_TTL_MS = 5 * 60 * 1000;

type RemoteConfigState = Record<string, never>;

export class AppRemoteConfigCubit extends Cubit<RemoteConfigState> {
  private client: CmsApiClient;

  constructor() {
    super({});
    this.client = createCmsClient(envVariables.CMS_API_BASE_URL);
  }

  private fetchJourneyContentFromApi = async (
    journey: string,
    language: Language
  ): Promise<JourneyData> => {
    const response = await this.client.api.educational.program.$get({
      query: {
        language,
        journey
      }
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(
        `CMS API request failed (${response.status}): ${errorText}`
      );
    }

    const responseJson = (await response.json()) as unknown as {
      data: JourneyData;
    };
    return responseJson.data;
  };

  loadJourneyContent = async (
    journey: string,
    params?: {
      language?: Language;
    }
  ): Promise<JourneyData> => {
    const { language = getSupportedUserLanguage() } = params || {};
    const cacheKey = `content:${journey}:${language}`;
    const requestOwnerId = StorageController.activeUserId;

    let cached: Awaited<ReturnType<typeof DbJourneyContentService.get>>;
    try {
      cached = await DbJourneyContentService.get(journey, language);
    } catch (error) {
      reportErrorSentry(error);
    }
    const cachedData = cached?.data as unknown as JourneyData | undefined;

    const refresh = async (): Promise<JourneyData | undefined> => {
      let fresh: JourneyData;
      try {
        fresh = await this.fetchJourneyContentFromApi(journey, language);
      } catch (error) {
        if (cachedData) {
          reportErrorSentry(error);
          return undefined;
        }
        throw error;
      }

      try {
        await DbJourneyContentService.putForOwner(
          requestOwnerId,
          journey,
          language,
          fresh as unknown as Record<string, unknown>,
          Date.now(),
          cacheKey
        );
      } catch (error) {
        reportErrorSentry(error);
      }

      return fresh;
    };

    if (cachedData) {
      let stale = true;
      try {
        stale = await JourneyMetaService.isStale(
          cacheKey,
          JOURNEY_CONTENT_TTL_MS
        );
      } catch (error) {
        reportErrorSentry(error);
      }
      if (stale) {
        void refresh();
      }
      return cachedData;
    }

    const fresh = await refresh();
    return fresh as JourneyData;
  };

  loadEducationalContent = async (
    params: FeedRequestBody
  ): Promise<FeedData> => {
    const body = { json: params };
    const response = await this.client.api.educational.feed.$post(body);

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(
        `CMS API request failed (${response.status}): ${errorText}`
      );
    }

    const responseJson = (await response.json()) as unknown as Feed;
    return responseJson.data;
  };
}
